import {
    AlertTriangle,
    ArrowLeft,
    ArrowRight,
    BadgeIndianRupee,
    CalendarClock,
    CheckCircle2,
    ChevronLeft,
    Circle,
    Clock3,
    LoaderCircle,
    Plus,
    WalletCards,
} from 'lucide-react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { FaCircleUser } from 'react-icons/fa6'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import RejectAdvanceModal from './RejectAdvanceModal'
import StaffPickerModal from './StaffPickerModal'
import styles from './StoreAdvanceBookScreen.module.css'

const FILTER_TABS = [
    { key: 'ALL', label: 'All' },
    { key: 'PENDING_APPROVAL', label: 'Pending' },
    { key: 'ACTIVE', label: 'Active' },
    { key: 'CLOSED', label: 'Closed' },
    { key: 'WRITTEN_OFF', label: 'Written Off' },
    { key: 'REJECTED', label: 'Rejected' },
]

const STATUS_CONFIG = {
    PENDING_APPROVAL: { label: 'Pending Approval', color: '#E65100', bg: '#FFF3E0' },
    ACTIVE: { label: 'Active', color: '#2E7D32', bg: '#E8F5E9' },
    CLOSED: { label: 'Closed', color: '#546E7A', bg: '#ECEFF1' },
    WRITTEN_OFF: { label: 'Written Off', color: '#6A1B9A', bg: '#F3E5F5' },
    FORECLOSED: { label: 'Foreclosed', color: '#1565C0', bg: '#E3F2FD' },
    REJECTED: { label: 'Rejected', color: '#C62828', bg: '#FFEBEE' },
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount) => {
    const num = parseFloat(amount)
    if (!isFinite(num)) return '—'
    return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

const formatYearMonth = (ym) => {
    if (!ym || typeof ym !== 'string' || !ym.includes('-')) return '—'
    const [year, month] = ym.split('-')
    const monthIndex = parseInt(month, 10) - 1
    if (!isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return ym
    return `${MONTH_NAMES[monthIndex]} ${year}`
}

const getTabCount = (key, data) => {
    if (!data) return 0
    switch (key) {
        case 'ALL':
            return (
                (data.totalActiveAdvances || 0) +
                (data.totalPendingApproval || 0) +
                (data.totalClosed || 0) +
                (data.totalWrittenOff || 0) +
                (data.totalForeclosed || 0) +
                (data.totalRejected || 0)
            )
        case 'PENDING_APPROVAL':
            return data.totalPendingApproval || 0
        case 'ACTIVE':
            return data.totalActiveAdvances || 0
        case 'CLOSED':
            return data.totalClosed || 0
        case 'WRITTEN_OFF':
            return data.totalWrittenOff || 0
        case 'REJECTED':
            return data.totalRejected || 0
        default:
            return 0
    }
}

const StoreAdvanceBookScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [bookData, setBookData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState('ALL')
    const [currentPage, setCurrentPage] = useState(1)
    const [selectedAdvance, setSelectedAdvance] = useState(null)
    const [rejectModalOpen, setRejectModalOpen] = useState(false)
    const [pickerOpen, setPickerOpen] = useState(false)

    const showToast = (message, type = 'info') => {
        toast[type](message, { position: 'bottom-center' })
    }

    useEffect(() => {
        setCurrentPage(1)
    }, [statusFilter])

    const fetchAdvanceBook = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'getStoreAdvanceBook', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    statusFilter,
                    page: currentPage,
                    pageSize: 20,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setBookData(respo.response)
                return
            }

            if (respo?.message === 'tokenExpired' || respo?.errorCode === 'tokenExpired') {
                authCtx.logout()
                return
            }

            showToast(respo?.message || 'Failed to load advance book.', 'error')
        } catch {
            showToast('Network error.', 'error')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchAdvanceBook()
    }, [statusFilter, currentPage])

    const pagination = bookData?.pagination || {}
    const advances = bookData?.advances || []

    const pendingBadgeTone = useMemo(() => {
        return (bookData?.totalPendingApproval || 0) > 0 ? styles.pendingHighlight : ''
    }, [bookData?.totalPendingApproval])

    const openRejectModal = (advance) => {
        setSelectedAdvance(advance)
        setRejectModalOpen(true)
    }

    const closeRejectModal = () => {
        setRejectModalOpen(false)
        setSelectedAdvance(null)
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <h1 className={styles.title}>Advance Book</h1>
                <button
                    type="button"
                    className={styles.newAdvanceBtn}
                    onClick={() => setPickerOpen(true)}
                >
                    <Plus size={16} /> New Advance
                </button>
            </div>

            {isLoading ? (
                <div className={styles.loaderWrap}>
                    <LoaderCircle size={30} className={styles.spin} />
                    <p>Loading advance book...</p>
                </div>
            ) : (
                <>
                    <div className={styles.content}>
                        <div className={styles.summaryGrid}>
                            <div className={styles.summaryCard}>
                                <div className={styles.summaryLabel}>Outstanding</div>
                                <div className={styles.summaryValue}>₹{formatINR(bookData?.totalOutstandingAmount || 0)}</div>
                                <div className={styles.summarySub}>
                                    {(bookData?.totalActiveAdvances || 0)} active advance(s)
                                </div>
                            </div>

                            <div className={styles.summaryCard}>
                                <div className={styles.summaryLabel}>Monthly EMI Load</div>
                                <div className={styles.summaryValue}>₹{formatINR(bookData?.totalMonthlyEMICommitment || 0)}</div>
                                <div className={styles.summarySub}>(FIXED_EMI only)</div>
                            </div>

                            <div className={`${styles.summaryCard} ${pendingBadgeTone}`}>
                                <div className={styles.summaryLabel}>Pending Approval</div>
                                <div className={styles.summaryValue}>{bookData?.totalPendingApproval || 0}</div>
                                <div className={styles.summarySub}>Awaiting your decision</div>
                            </div>
                        </div>

                        <div className={styles.tabsRow}>
                            {FILTER_TABS.map((tab) => {
                                const count = getTabCount(tab.key, bookData)
                                const isActive = statusFilter === tab.key
                                return (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        onClick={() => setStatusFilter(tab.key)}
                                        className={`${styles.tabBtn} ${isActive ? styles.tabBtnActive : ''}`}
                                    >
                                        <span>{tab.label}</span>
                                        <span className={`${styles.tabCount} ${isActive ? styles.tabCountActive : ''}`}>
                                            {count}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>

                        {advances.length === 0 ? (
                            <div className={styles.emptyState}>
                                <AlertTriangle size={40} />
                                <h3>No advances found</h3>
                                <p>No records match the selected filter.</p>
                            </div>
                        ) : (
                            <div className={styles.rowsWrap}>
                                {advances.map((advance) => {
                                    const statusCfg = STATUS_CONFIG[advance.advanceStatus] || {
                                        label: advance.advanceStatus,
                                        color: '#475569',
                                        bg: '#E2E8F0',
                                    }

                                    const isActiveAdvance = advance.advanceStatus === 'ACTIVE'
                                    const isPendingApproval = advance.advanceStatus === 'PENDING_APPROVAL'

                                    return (
                                        <div key={advance.advanceIdentifier} className={styles.rowCard}>
                                            <button
                                                type="button"
                                                className={styles.rowMain}
                                                onClick={() =>
                                                    navigate(`/payroll/advance/staff-ledger/${advance.staffIdentifier}`, {
                                                        state: {
                                                            staffName: advance.staffName,
                                                        },
                                                    })
                                                }
                                            >
                                                <div className={styles.identityBlock}>
                                                    <FaCircleUser className={styles.avatarIcon} />
                                                    <div>
                                                        <div className={styles.staffName}>{advance.staffName || 'Staff'}</div>
                                                        {advance.designationLabel ? (
                                                            <div className={styles.designation}>{advance.designationLabel}</div>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                <div className={styles.amountStatusRow}>
                                                    <div className={styles.amountText}>₹{formatINR(advance.disbursedAmount || advance.requestedAmount || 0)}</div>
                                                    <span
                                                        className={styles.statusBadge}
                                                        style={{ color: statusCfg.color, backgroundColor: statusCfg.bg }}
                                                    >
                                                        {statusCfg.label}
                                                    </span>
                                                </div>

                                                {isActiveAdvance ? (
                                                    <div className={styles.progressWrap}>
                                                        <div className={styles.progressText}>
                                                            ₹{formatINR(advance.totalRepaid || 0)} repaid of ₹{formatINR(advance.disbursedAmount || 0)} ({Math.round(advance.progressPercent || 0)}%)
                                                        </div>
                                                        <div className={styles.progressTrack}>
                                                            <div
                                                                className={styles.progressFill}
                                                                style={{ width: `${Math.max(0, Math.min(100, Math.round(advance.progressPercent || 0)))}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : null}

                                                <div className={styles.metaGrid}>
                                                    <div className={styles.metaItem}>
                                                        <BadgeIndianRupee size={14} />
                                                        <span>
                                                            EMI:{' '}
                                                            {advance.repaymentType === 'PERCENT_OF_NET'
                                                                ? `${advance.emiPercent || 0}% of net`
                                                                : `₹${formatINR(advance.monthlyEMI || 0)}/month`}
                                                        </span>
                                                    </div>

                                                    {isActiveAdvance && advance.estimatedClosureMonth ? (
                                                        <div className={styles.metaItem}>
                                                            <CalendarClock size={14} />
                                                            <span>Closes: {formatYearMonth(advance.estimatedClosureMonth)}</span>
                                                        </div>
                                                    ) : null}

                                                    {advance.createdOn ? (
                                                        <div className={styles.metaItem}>
                                                            <Clock3 size={14} />
                                                            <span>Created: {advance.createdOn}</span>
                                                        </div>
                                                    ) : null}
                                                </div>

                                                {advance.requestRemarks ? (
                                                    <div className={styles.reasonText}>“{advance.requestRemarks}”</div>
                                                ) : null}
                                            </button>

                                            <div className={styles.actionRow}>
                                                {isPendingApproval ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={`${styles.actionBtn} ${styles.approveBtn}`}
                                                            onClick={() => navigate(`/payroll/advance/${advance.advanceIdentifier}/approve`, { state: { advance } })}
                                                        >
                                                            <CheckCircle2 size={14} /> Approve
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`${styles.actionBtn} ${styles.rejectBtn}`}
                                                            onClick={() => openRejectModal(advance)}
                                                        >
                                                            <Circle size={14} /> Reject
                                                        </button>
                                                    </>
                                                ) : isActiveAdvance ? (
                                                    <button
                                                        type="button"
                                                        className={`${styles.actionBtn} ${styles.viewBtn}`}
                                                        onClick={() =>
                                                            navigate(`/payroll/advance/staff-ledger/${advance.staffIdentifier}`, {
                                                                state: {
                                                                    staffName: advance.staffName,
                                                                },
                                                            })
                                                        }
                                                    >
                                                        <WalletCards size={14} /> View Details <ArrowRight size={14} />
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className={`${styles.actionBtn} ${styles.viewBtn}`}
                                                        onClick={() =>
                                                            navigate(`/payroll/advance/staff-ledger/${advance.staffIdentifier}`, {
                                                                state: {
                                                                    staffName: advance.staffName,
                                                                },
                                                            })
                                                        }
                                                    >
                                                        <WalletCards size={14} /> View History <ArrowRight size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        <div className={styles.paginationRow}>
                            <button
                                type="button"
                                className={styles.pageBtn}
                                disabled={!pagination?.hasPrevPage}
                                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                            >
                                <ArrowLeft size={14} /> Prev
                            </button>

                            <div className={styles.pageInfo}>
                                Page {pagination?.page || 1} of {Math.max(1, pagination?.totalPages || 1)}
                            </div>

                            <button
                                type="button"
                                className={styles.pageBtn}
                                disabled={!pagination?.hasNextPage}
                                onClick={() => setCurrentPage((prev) => prev + 1)}
                            >
                                Next <ArrowRight size={14} />
                            </button>
                        </div>
                    </div>
                </>
            )}

            <RejectAdvanceModal
                isOpen={rejectModalOpen}
                advance={selectedAdvance}
                onClose={closeRejectModal}
                onSuccess={() => {
                    closeRejectModal()
                    fetchAdvanceBook()
                }}
            />

            <StaffPickerModal
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={(staff) => {
                    setPickerOpen(false)
                    navigate(`/payroll/advance/${staff.staffIdentifier}/create`, {
                        state: { staffName: staff.staffName },
                    })
                }}
            />
        </div>
    )
}

export default StoreAdvanceBookScreen